import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Modal, Radio, RadioButtons, Row } from 'elmo-elements';
import { useDispatch } from 'react-redux';
import {
  useLazyGetElearningJsonQuery,
  useLazyGetFramesQuery,
  useLazyGetManifestQuery,
  useLazyGetSignedCookiesAdminQuery,
} from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ElearningDataType } from '@/modules/types/elearning';
import { exportToDocument } from '@/lib/exportToDocument';
import {
  isElearningPublishedToLiveNew,
  isElearningPublishedToReview,
} from '@/lib/util';
import DocxPreviewModal from './DocxPreviewModal';
import './ExportModuleDialog.scss';

export type ExportSource = 'draft' | 'review' | 'live';

export type ExportModuleDialogProps = {
  isOpened: boolean;
  onClose: () => void;
  isBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  elearningData: ElearningDataType;
  canExportModuleToWordDocument: boolean;
};

const ExportModuleDialog = ({
  isOpened,
  onClose,
  isBusy,
  onBusyChange,
  elearningData,
  canExportModuleToWordDocument,
}: ExportModuleDialogProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { learningElaExportToDocument } = useFlags();
  const [getFrames] = useLazyGetFramesQuery();
  const [triggerManifest] = useLazyGetManifestQuery();
  const [triggerSignedCookiesAdmin] = useLazyGetSignedCookiesAdminQuery();
  const [triggerElearningJson] = useLazyGetElearningJsonQuery();
  const [exportSource, setExportSource] = useState<ExportSource>('draft');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDocPreviewBusy, setIsDocPreviewBusy] = useState(false);

  const canSelectReview = useMemo(
    () => isElearningPublishedToReview(+elearningData.statusMetadata.status),
    [elearningData.statusMetadata.status],
  );

  const canSelectLive = useMemo(
    () => isElearningPublishedToLiveNew(elearningData.statusMetadata),
    [elearningData.statusMetadata],
  );

  const showSourceSelection = useMemo(
    () => canSelectReview || canSelectLive,
    [canSelectReview, canSelectLive],
  );

  useEffect(() => {
    if (!isOpened) {
      setExportSource('draft');
    }
  }, [isOpened]);

  const flashMessage = useCallback(
    (message: string): void => {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: { isActive: true, message },
      });
    },
    [dispatch],
  );

  const validateExport = useCallback((): boolean => {
    if (!canExportModuleToWordDocument) {
      flashMessage('You do not have permission to export this module.');
      onClose();
      return false;
    }
    if (!learningElaExportToDocument) {
      flashMessage('Export to document feature is not enabled.');
      onClose();
      return false;
    }
    if (
      (exportSource === 'review' && !canSelectReview) ||
      (exportSource === 'live' && !canSelectLive)
    ) {
      flashMessage(
        `The selected export source ${exportSource} is not available for this module.`,
      );
      onClose();
      return false;
    }
    return true;
  }, [
    canExportModuleToWordDocument,
    canSelectLive,
    canSelectReview,
    exportSource,
    flashMessage,
    learningElaExportToDocument,
    onClose,
  ]);

  const runDownload = useCallback(async (): Promise<void> => {
    if (!validateExport()) return;

    onBusyChange(true);

    try {
      if (exportSource === 'draft') {
        const frames = await getFrames({ elearningId: elearningData.id });
        if (!frames.isSuccess || !frames.data) {
          flashMessage('Export failed. Please try again.');
          return;
        }
        try {
          await exportToDocument(frames.data, elearningData);
          flashMessage('Module exported successfully');
        } catch {
          flashMessage('Export failed. Please try again.');
        }
        return;
      }

      const type = exportSource;
      try {
        const manifestData = await triggerManifest({
          type,
          tenantId: elearningData.tenantId,
          elearningId: elearningData.id,
        }).unwrap();

        if (!manifestData?.Item) {
          flashMessage(
            'Published content is not available for this module yet.',
          );
          return;
        }

        const item = manifestData.Item;

        await triggerSignedCookiesAdmin({
          tenantId: elearningData.tenantId,
          type,
          elearningId: elearningData.id,
          ...(item.elearningIdentifier != null && {
            elearningIdentifier: `${item.elearningIdentifier}`,
          }),
          ...(item.quizIdentifier != null && {
            quizIdentifier: `${item.quizIdentifier}`,
          }),
          ...(item.elearningIdentifier != null && {
            themeId: `${item.themeUUID}`,
          }),
        }).unwrap();

        const published = await triggerElearningJson({
          tenantId: elearningData.tenantId,
          type,
          entityId: elearningData.id,
          identifier: item.elearningIdentifier,
          entity: 'elearnings',
          contentType: 'published-content',
        }).unwrap();

        const { frames: publishedFrames = [], ...elearningPayload } = published;
        await exportToDocument(publishedFrames, elearningPayload);
        flashMessage('Module exported successfully');
      } catch {
        flashMessage('Export failed. Please try again.');
      }
    } finally {
      onBusyChange(false);
      onClose();
    }
  }, [
    elearningData,
    exportSource,
    flashMessage,
    getFrames,
    onBusyChange,
    onClose,
    triggerElearningJson,
    triggerManifest,
    triggerSignedCookiesAdmin,
    validateExport,
  ]);

  const handleDownloadClick = (): void => {
    runDownload().catch(() => undefined);
  };

  const handleSourceChange = (value: string | number): void => {
    setExportSource(value as ExportSource);
  };

  return (
    <>
      <Modal
        id="ExportModuleDialog"
        type="small"
        isOpened={isOpened}
        closeModal={() => !isBusy && onClose()}
        title="Export to document"
        closeLabel="Close"
      >
        <div className="export-to-document__content">
          <p>
            {showSourceSelection
              ? 'Choose content to export and download from draft, review, or live.'
              : "Download a Word file with this module's current draft content."}
          </p>
          {showSourceSelection && (
            <div className="export-to-document__source">
              <span className="export-to-document__source-label">
                Content source
              </span>
              <RadioButtons
                selected={exportSource}
                type="outlined"
                onChange={handleSourceChange}
              >
                <Radio value="draft">Draft</Radio>
                {canSelectReview && <Radio value="review">Review</Radio>}
                {canSelectLive && <Radio value="live">Live</Radio>}
              </RadioButtons>
            </div>
          )}
          <Row className="export-to-document__actions">
            <Col isAuto={true}>
              <Button
                type="primary"
                className="export-to-document__button"
                isDisabled={isBusy}
                onClick={handleDownloadClick}
              >
                Download
              </Button>
            </Col>
          </Row>
        </div>
      </Modal>
      {/* We are leaving this UI here in case we need it for faster debugging or improvement developments in the future.
        all we need is add a preview button to show this modal, if needed. */}
      <DocxPreviewModal
        isOpened={isPreviewOpen}
        onClose={() => {
          setIsDocPreviewBusy(false);
          setIsPreviewOpen(false);
        }}
        isBusy={isDocPreviewBusy}
        onBusyChange={setIsDocPreviewBusy}
        elearningData={elearningData}
      />
    </>
  );
};

export default ExportModuleDialog;
